import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createJSONRPCErrorResponse, createJSONRPCRequest, createJSONRPCSuccessResponse, isJSONRPCRequest, isJSONRPCResponse, JSONRPCErrorCode } from 'json-rpc-2.0';

/**
 * Strongly typed RPC request, sent over JavaScript contexts.
 *
 * @description Represents a delegated method call using the command pattern.
 * @see {@link https://gameprogrammingpatterns.com/command.html}
 *
 * @description Data is serialised over-the-wire in the JSON RPC 2.0 protocol: {@link https://www.jsonrpc.org/specification}.
 *
 * @example
 * ```json
 *	{
 *  	"jsonrpc": "2.0",
 *  	"id": "rpc:app-content:app-chat:FindCommand:",
 *  	"method": "FindCommand",
 *  	"params": {
 *			"sourceId": "app-content",
 *			"targetId": "app-chat"
 *		}
 *	}
 * ```
 */

/**
 * Strongly-typed RPC response, sent over JavaScript contexts.
 *
 * @description Represents the result of a delegated method call.
 * @description Data is serialised over-the-wire in the JSON RPC 2.0 protocol: {@link https://www.jsonrpc.org/specification}.
 *
 * @example
 * ```json
 *	{
 *		"jsonrpc": "2.0",
 *		"id": "rpc:app-content:app-chat:PingPongCommand:pKI_V2Wn9-wb3kQehoDyB",
 *		"result": "pong",
 *		"params": {
 *			"sourceId": "app-chat",
 *			"targetId": "app-content"
 *		}
 *	}
 * ```
 */

/**
 * Strongly-typed RPC message which can be sent over-the-wire between JavaScript contexts.
 */

/**
 * Unique identifier for an RPC message.
 */

/**
 * A unique ID for this specific command invocation.
 * @param commandClass
 * @param commandProperties
 * @returns unique ID string with parts separated by `:`
 */
export function createInvocationId(commandClass, commandProperties) {
  var _commandProperties$_i;
  var parts = ['rovo-bridge-api', commandProperties.senderId, commandProperties.receiverId, commandClass.identifier, (_commandProperties$_i = commandProperties._invocationId) !== null && _commandProperties$_i !== void 0 ? _commandProperties$_i : 'unspecified-id'];
  return parts.join(':');
}

// ----------------------------------------------------
// #region  Raw data -> RPC shape (on sender's side)
// ----------------------------------------------------

/**
 * Converts a command invocation to an {@link RPCRequest}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#request_object}.
 *
 * @param id unique ID for this command invocation
 * @param commandClass class of the command to be invoked
 * @param commandProperties parameters of the command
 * @returns {RPCRequest}
 */
export function rpcCommand(id, commandClass, commandProperties) {
  return createJSONRPCRequest(id, commandClass.identifier, commandProperties);
}

/**
 * Converts a successful command invocation result to an {@link RPCResponse}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#response_object}
 *
 * @param id unique ID for this command invocation
 * @param senderId client which handled the request, and provided the response
 * @param receiverId client which sent the request, and is receiving the response
 * @param result result of the invocation, as handled by the sender
 * @returns {RPCResponse}
 */
export function rpcSuccess(id, senderId, receiverId, result) {
  var response = createJSONRPCSuccessResponse(id, result);
  return _objectSpread(_objectSpread({}, response), {}, {
    params: {
      senderId: senderId,
      receiverId: receiverId
    }
  });
}

/**
 * Converts an unsuccessful command invocation result to an {@link RPCResponse}.
 *
 * @description For more, @see {@link https://www.jsonrpc.org/specification#error_object}
 *
 * @param id unique ID for this command invocation
 * @param senderId client which handled the request, and provided the response
 * @param receiverId client which sent the request, and is receiving the response
 * @param result result of the invocation, as handled by the sender
 * @returns {RPCResponse}
 */
export function rpcError(id, senderId, receiverId) {
  var code = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : JSONRPCErrorCode.InternalError;
  var error = arguments.length > 4 ? arguments[4] : undefined;
  var response = createJSONRPCErrorResponse(id, code, error.message, error.stack);
  return _objectSpread(_objectSpread({}, response), {}, {
    params: {
      senderId: senderId,
      receiverId: receiverId
    }
  });
}

// ----------------------------------------------------
// #region  Raw data -> RPC shape (on receiver's side)
// ----------------------------------------------------

/**
 * Type guard which checks if a given shape is an {@link RPCRequest}.
 *
 * @description MUST provide an ID for resiliency and observability purposes.
 *
 * @param request a shape which is possibly an RPC request
 * @returns whether {@link request} is an {@link RPCRequest} or not
 */
export function isRPCRequest(request) {
  return Boolean(isJSONRPCRequest(request) && request.id && typeof request.id === 'string');
}

/**
 * Type guard which checks if a given shape is an {@link RPCResponse}.
 *
 * @description MUST provide an ID for deferred awaiting on the result of an invocation.
 *
 * @param request a shape which is possibly an RPC request
 * @returns whether {@link request} is an {@link RPCRequest} or not
 */
export function isRPCResponse(response) {
  return Boolean(isJSONRPCResponse(response) && response.id && typeof response.id === 'string');
}